// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

/**
 * Centralized enum for all operation types used across the platform. This enum
 * should be used instead of string literals or separate type definitions.
 */
export enum OperationType {
  /** Buy operation - purchasing an asset */
  BUY = 'BUY',
  /** Sell operation - selling an asset */
  SELL = 'SELL',
  /** Transfer operation - transferring assets between positions */
  TRANSFER = 'TRANSFER',
  /** Hold operation - maintaining current position without changes */
  HOLD = 'HOLD',
}

/**
 * Type alias for the OperationType enum values. Use this when you need a type that
 * represents any operation type.
 */
export type OperationTypeValue = OperationType;

/**
 * Type for operations that support BUY and SELL only. Commonly used in trading
 * operations.
 */
export type BuySellOperation = OperationType.BUY | OperationType.SELL;

/**
 * Type for operations that support BUY, SELL, and TRANSFER. Commonly used in
 * rebalancing and portfolio operations.
 */
export type BuySellTransferOperation =
  | OperationType.BUY
  | OperationType.SELL
  | OperationType.TRANSFER;

/**
 * Type for operations that support BUY, SELL, and HOLD. Commonly used in trader
 * operations.
 */
export type BuySellHoldOperation =
  | OperationType.BUY
  | OperationType.SELL
  | OperationType.HOLD;

/**
 * Helper function to normalize operation type strings to the enum. Converts
 * lowercase strings to uppercase and validates against the enum.
 *
 * @param operation - The operation string to normalize (can be uppercase or
 *   lowercase). Can be undefined or null, in which case SELL is returned as
 *   default.
 * @returns The normalized OperationType enum value
 * @throws Error if the operation is not a valid OperationType
 */
export function normalizeOperationType(
  operation: string | undefined | null,
): OperationType {
  if (!operation) {
    return OperationType.SELL; // Default fallback
  }
  const upperOperation = operation.toUpperCase() as OperationType;
  if (Object.values(OperationType).includes(upperOperation)) {
    return upperOperation;
  }
  throw new Error(
    `Invalid operation type: ${operation}. Must be one of: ${Object.values(
      OperationType,
    ).join(', ')}`,
  );
}

/**
 * Helper function to check if a string is a valid operation type.
 *
 * @param operation - The operation string to check. Can be undefined or null.
 * @returns True if the operation is a valid OperationType
 */
export function isValidOperationType(
  operation: string | undefined | null,
): boolean {
  if (!operation) {
    return false;
  }
  return Object.values(OperationType).includes(
    operation.toUpperCase() as OperationType,
  );
}
